import { db, isFirebaseInitialized, ensureFirebaseInitialized } from './config';
import firebase from 'firebase/compat/app';

const CATEGORIES_COLLECTION = 'categories';

// Helper function to check if categories are default categories (to detect and clear them)
const isDefaultCategory = (category) => {
  const defaultIds = ['kanchipuram', 'venkatagiri', 'mysore', 'kalamkari', 'kerala'];
  return defaultIds.includes(category.id);
};

// Helper function to check if cache contains default categories
const cacheContainsDefaults = (cache) => {
  if (!cache || cache.length === 0) return false;
  return cache.some(cat => isDefaultCategory(cat));
};

// Categories cache for faster loading
let categoriesCache = null;
let categoriesCacheTime = null;
const CATEGORIES_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Subscribe to categories - Real-time listener
export const subscribeToCategories = (callback) => {
  let unsubscribe = null;
  let isUnsubscribed = false;

  // Wait for Firebase to be initialized before setting up listener
  ensureFirebaseInitialized()
    .then(() => {
      // Wait a bit more to ensure db is ready
      let retries = 0;
      const maxRetries = 20; // 4 seconds max wait
      const setupSubscription = () => {
        if (isUnsubscribed) return;

        if (isFirebaseInitialized() && db) {
          const categoriesRef = db.collection(CATEGORIES_COLLECTION);

          // Try with orderBy first, fallback to simple query if it fails
          let query = categoriesRef.orderBy('displayOrder', 'asc');

          const setupListener = (q) => {
            return q.onSnapshot(
              (querySnapshot) => {
                if (isUnsubscribed) return;

                const categories = [];
                querySnapshot.forEach((snap) => {
                  const data = snap.data();
                  const category = {
                    docId: snap.id,
                    ...data,
                    id: data && data.id ? data.id : snap.id
                  };

                  // Only add if it's not a default category
                  if (!isDefaultCategory(category)) {
                    categories.push(category);
                  }
                });

                // Update cache
                categoriesCache = categories;
                categoriesCacheTime = Date.now();
                callback(categories);
              },
              (error) => {
                if (isUnsubscribed) return;

                // If orderBy fails, try without ordering
                if (error.code === 'failed-precondition' && query === categoriesRef.orderBy('displayOrder', 'asc')) {
                  console.warn('⚠️ OrderBy failed, trying without ordering');
                  query = categoriesRef;
                  unsubscribe = setupListener(query);
                } else {
                  console.error('❌ Error in categories subscription:', error);
                  callback([]);
                }
              }
            );
          };

          unsubscribe = setupListener(query);
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(setupSubscription, 200);
        } else {
          console.warn('⚠️ Firebase not initialized after waiting, using fallback');
          callback([]);
        }
      };

      setupSubscription();
    })
    .catch((error) => {
      console.error('❌ Error initializing Firebase for categories:', error);
      if (!isUnsubscribed) {
        callback([]);
      }
    });

  // Return unsubscribe function
  return () => {
    isUnsubscribed = true;
    if (unsubscribe) {
      unsubscribe();
    }
  };
};

// Get all categories - ONLY from Firestore, never return default categories
export const getCategories = async () => {
  try {
    // Check cache first for faster response
    if (categoriesCache && categoriesCacheTime && (Date.now() - categoriesCacheTime) < CATEGORIES_CACHE_DURATION) {
      // CRITICAL: If cache contains default categories, clear it and fetch from Firestore
      if (cacheContainsDefaults(categoriesCache)) {
        console.warn('⚠️ Cache contains default categories, clearing cache and fetching from Firestore');
        categoriesCache = null;
        categoriesCacheTime = null;
      } else {
        return categoriesCache;
      }
    }

    // Check if Firebase is initialized
    if (!isFirebaseInitialized() || !db) {
      console.warn("⚠️ Firebase not initialized, returning empty array - categories must come from Firestore");
      return []; // Never return default categories
    }

    const categoriesRef = db.collection(CATEGORIES_COLLECTION);

    // Reduced timeout for faster failure detection (5 seconds instead of 10)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Categories request timeout')), 5000);
    });

    let querySnapshot;
    try {
      const queryPromise = categoriesRef.orderBy('displayOrder', 'asc').get();
      querySnapshot = await Promise.race([queryPromise, timeoutPromise]);
    } catch (queryError) {
      // If orderBy fails (field might not exist), try without ordering
      console.warn('⚠️ OrderBy failed, trying without ordering:', queryError.message);
      const queryPromise = categoriesRef.get();
      querySnapshot = await Promise.race([queryPromise, timeoutPromise]);
    }

    const categories = [];
    querySnapshot.forEach((snap) => {
      // Expose BOTH Firestore document id and logical category id
      // - docId: Firestore document id (used for updates/deletes)
      // - id: logical id stored in the document (used by the site for routing/filtering)
      const data = snap.data();
      const category = {
        docId: snap.id,
        ...data,
        // Preserve logical id if present; otherwise fall back to doc id
        id: data && data.id ? data.id : snap.id
      };

      // Only add if it's not a default category (safety check)
      if (!isDefaultCategory(category)) {
        categories.push(category);
      } else {
        console.warn(`⚠️ Skipping default category found in Firestore: ${category.id}`);
      }
    });

    // Only cache if we have actual categories from Firestore
    if (categories.length > 0) {
      categoriesCache = categories;
      categoriesCacheTime = Date.now();
      console.log(`✅ Fetched ${categories.length} categories from Firestore`);
    } else {
      // Clear cache if database is empty
      categoriesCache = null;
      categoriesCacheTime = null;
      console.log('ℹ️ No categories found in Firestore database');
    }

    return categories; // Will be empty array if no categories in Firestore
  } catch (error) {
    console.error('❌ Error fetching categories from Firestore:', error);

    // Return cached data if available, but only if it's from Firestore (not defaults)
    if (categoriesCache && !cacheContainsDefaults(categoriesCache)) {
      console.warn('⚠️ Using cached Firestore categories due to error');
      return categoriesCache;
    }

    // Clear cache if it contains defaults
    if (categoriesCache && cacheContainsDefaults(categoriesCache)) {
      console.warn('⚠️ Cache contains default categories, clearing cache');
      categoriesCache = null;
      categoriesCacheTime = null;
    }

    // Return empty array - never return default categories
    console.warn('⚠️ No categories available from Firestore, returning empty array');
    return [];
  }
};

// Get cached categories (for immediate UI rendering)
export const getCachedCategories = () => {
  return categoriesCache || [];
};

// Clear categories cache (useful when categories are updated)
export const clearCategoriesCache = () => {
  // Also check if cache contains defaults before clearing
  if (categoriesCache && cacheContainsDefaults(categoriesCache)) {
    console.warn('⚠️ Clearing cache that contains default categories');
  }
  categoriesCache = null;
  categoriesCacheTime = null;
};

// Get a single category by ID
export const getCategory = async (categoryId) => {
  try {
    const categoryRef = db.collection(CATEGORIES_COLLECTION).doc(categoryId);
    const categorySnap = await categoryRef.get();

    if (categorySnap.exists) {
      const data = categorySnap.data();
      const category = {
        docId: categorySnap.id,
        ...data,
        id: data && data.id ? data.id : categorySnap.id
      };
      return category;
    } else {
      return null;
    }
  } catch (error) {
    console.error('❌ Error fetching category:', error);
    throw error;
  }
};

// Add a new category
export const addCategory = async (categoryData) => {
  try {
    // Validate required fields
    if (!categoryData.name || !categoryData.description || !categoryData.route) {
      throw new Error('Missing required fields: name, description, route');
    }

    const categoryRef = db.collection(CATEGORIES_COLLECTION);
    const newCategory = {
      ...categoryData,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp()
    };

    const docRef = await categoryRef.add(newCategory);
    clearCategoriesCache();
    return docRef.id;
  } catch (error) {
    console.error('❌ Error adding category:', error);
    throw error;
  }
};

// Update an existing category
export const updateCategory = async (docId, categoryData) => {
  try {
    const categoryRef = db.collection(CATEGORIES_COLLECTION).doc(docId);
    const updateData = {
      ...categoryData,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp()
    };

    await categoryRef.update(updateData);
    clearCategoriesCache();
    return docId;
  } catch (error) {
    console.error('❌ Error updating category:', error);
    throw error;
  }
};

// Delete a category
export const deleteCategory = async (docId) => {
  try {
    const categoryRef = db.collection(CATEGORIES_COLLECTION).doc(docId);
    await categoryRef.delete();
    clearCategoriesCache();
    return docId;
  } catch (error) {
    console.error('❌ Error deleting category:', error);
    throw error;
  }
};

// Initialize default categories (run once to populate database)
export const initializeDefaultCategories = async () => {
  try {
    const defaultCategories = [
      {
        name: 'Kanchipuram',
        description: 'Temple borders and jewel-toned silks from Kanchipuram.',
        image: '/assets/kanjivaram.jpeg',
        route: '/shop/kanchipuram',
        displayOrder: 1,
        isActive: true
      },
      {
        name: 'Venkatagiri Pattu Saree',
        description: 'Traditional silk sarees with intricate zari work from Venkatagiri.',
        image: '/assets/banarasi.jpeg',
        route: '/shop/venkatagiri',
        displayOrder: 2,
        isActive: true
      },
      {
        name: 'Mysore Silk Sarees',
        description: 'Luxurious silk sarees with traditional Mysore craftsmanship.',
        image: '/assets/tussar.jpeg',
        route: '/shop/mysore',
        displayOrder: 3,
        isActive: true
      },
      {
        name: 'Kalamkari Silk Sarees',
        description: 'Hand-painted silk sarees with traditional Kalamkari art.',
        image: '/assets/patola.jpeg',
        route: '/shop/kalamkari',
        displayOrder: 4,
        isActive: true
      },
      {
        name: 'Kerala Kasavu Sarees',
        description: 'Traditional Kerala sarees with golden borders and elegant drape.',
        image: '/assets/linen.jpeg',
        route: '/shop/kerala',
        displayOrder: 5,
        isActive: true
      }
    ];

    // Check if categories already exist
    const existingCategories = await getCategories();
    if (existingCategories.length > 0) {
      return { message: 'Categories already initialized', count: existingCategories.length };
    }

    // Add default categories
    const results = [];
    for (const category of defaultCategories) {
      const categoryId = await addCategory(category);
      results.push(categoryId);
    }
    return { message: 'Default categories initialized', count: results.length, ids: results };
  } catch (error) {
    console.error('❌ Error initializing default categories:', error);
    throw error;
  }
};
